<template>
  <v-dialog
    :value="value"
    max-width="650px"
    @input="close">
    <v-card>
      <v-card-title>
        <span class="headline">{{ $t('heading') }}</span>
      </v-card-title>
      <v-card-text>

        <v-form ref="holidaysForm">

          <p
            v-for="(error, index) in logicErrors"
            :key="index"
            class="mx-1 error--text"
          >
            {{ error }}
          </p>

          <date-picker
            v-model="from"
            :label="$t('from')"
            :rules="[$rules.isRequired]"
            :allowed-dates="allowedBeginDates"
            :error-messages="errorMessages.from"/>

          <date-picker
            v-model="to"
            :label="$t('to')"
            :rules="[$rules.isRequired]"
            :allowed-dates="allowedEndDates"
            :error-messages="errorMessages.to"/>

        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn
          flat
          @click="close">{{ $t('cancel') }}
        </v-btn>
        <disabled-user-button>
          <v-btn
            slot-scope="{isDisabledForUser}"
            :disabled="!isValid || isDisabledForUser || isWaitingForServerResponse"
            :loading="isWaitingForServerResponse"
            color="primary"
            @click="saveEntry"
          >
            {{ $t('save') }}
          </v-btn>
        </disabled-user-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import HolidaysApi from '@/api/HolidaysApi'
import MonthPicker from '@/components/global-components/inputs/MonthPicker'
import DisabledUserButton from '@/components/global-components/disabledUserButton/disabledUserButton'
import DatePicker from '@/components/global-components/inputs/DatePicker'

export default {
  name: 'EmployeeSettingsHolidaysEntryDialog',
  components: { DatePicker, DisabledUserButton, MonthPicker },
  props: ['value'],
  data () {
    return {
      from: null,
      to: null,
      isWaitingForServerResponse: false,
      isSnackbar: true,
      logicErrors: [],
      errorMessages: {}
    }
  },
  computed: {
    ...mapGetters('employee', ['currentEmployee']),
    momentBegin () {
      if (this.from) {
        return this.$moment(this.from, 'YYYY-MM-DD')
      }
    },
    momentEnd () {
      if (this.to) {
        return this.$moment(this.to, 'YYYY-MM-DD')
      }
    },
    isValid () {
      return Boolean(this.momentBegin && this.momentEnd)
    }
  },
  methods: {
    close () {
      this.$emit('input', false)
    },

    resetInitialData () {
      this.$refs.holidaysForm.reset()

      this.to = null
      this.from = null

      this.errorMessages = {}
      this.logicErrors = []
    },

    async saveEntry () {
      const from = this.momentBegin.format('YYYY-MM-DD')
      const to = this.momentEnd.format('YYYY-MM-DD')

      this.isWaitingForServerResponse = true

      try {
        const result = await HolidaysApi.setHolidays(this.currentEmployee.schema.id, from, to)
        await this.$emit('updateOnSuccess')
        this.close()
        this.$notification(result)
        this.resetInitialData()
      } catch (e) {
        if (e.errors.message) {
          this.logicErrors = e.errors.message
        } else {
          this.errorMessages = e.errors
        }
      }

      this.isWaitingForServerResponse = false
    },

    allowedBeginDates (val) {
      const $date = this.$moment(val, 'YYYY-MM-DD')
      const $now = this.$moment()

      const isInPast = $date.diff($now, 'years') < 1
      return isInPast
    },
    allowedEndDates (val) {
      const $date = this.$moment(val, 'YYYY-MM-DD')
      const $now = this.$moment()
      const isInPast = $date.diff($now, 'years') < 1
      let isAfterBegin = true

      if (this.from) {
        isAfterBegin = this.momentBegin.diff($date, 'days') < 0
      }
      return isInPast && isAfterBegin
    }
  },
  i18n: {
    messages: {
      en: {
        heading: 'New holiday period',
        from: 'First day of holiday',
        to: 'Last day of holiday',
        cancel: 'Cancel',
        save: 'Save'
      },
      tr: {
        heading: 'Yeni tatil aralığı',
        from: 'Tatilin ilk günü',
        to: 'Tatilin son günü',
        cancel: 'İptal',
        save: 'Kaydet'
      },
      de: {
        heading: 'Neuer Urlaubszeitraum',
        from: 'Erster Urlaubstag',
        to: 'Letzter Urlaubstag',
        cancel: 'Abbrechen',
        save: 'Speichern'
      },
      ru: {
        heading: 'Новый праздничный период',
        from: 'Первый день отпуска',
        to: 'Последний день отпуска',
        cancel: 'Отмена',
        save: 'Сохранить'
      }
    }
  }
}
</script>
